import {
  makeContentServicePostRequest,
  makeContentServiceGetRequest,
  makeContentServiceDeleteRequest,
} from 'infrastructure/contentService'
import {
  PromptsV2Response,
  PromptRunResponse,
  PipelineStatusResponse,
  PrepareResults,
  ProcessResults,
  ApplyResults,
  ProcessMode,
  BatchMode,
  StudentFacingOption,
} from './types'

/**
 * Start the prepare stage of the AI tree tool pipeline.
 *
 * @param treeId - The tree ID
 * @param treeVer - The tree version
 * @param copyTree - Optional flag to copy the tree (default: false)
 * @param processMode - The process mode (ai-autocorrect or title-autocorrect)
 * @param fieldTitle - Whether to process title field
 * @param fieldDescription - Whether to process description field
 * @param fieldBodyValue - Whether to process body value field
 * @param studentFacing - Student facing filter option
 * @returns Promise<unknown> - The response data from the prepare stage
 */
export async function startPrepare(
  treeId: string,
  treeVer: string,
  copyTree: boolean = false,
  processMode: ProcessMode = 'ai-autocorrect',
  fieldTitle: boolean = true,
  fieldDescription: boolean = true,
  fieldBodyValue: boolean = true,
  studentFacing: StudentFacingOption = 'all',
): Promise<unknown> {
  const response = await makeContentServicePostRequest(
    `content/v1/ai-tree-tool/${treeId}:${treeVer}/prepare/start`,
    {
      copy_tree: copyTree,
      process_mode: processMode,
      field_title: fieldTitle,
      field_description: fieldDescription,
      field_body_value: fieldBodyValue,
      student_facing: studentFacing,
    },
  )

  if (!response.ok) {
    const rawResponse = await response.text()
    const error = new Error(rawResponse) as Error & { status?: number }
    error.status = response.status
    throw error
  }

  try {
    const data = await response.json()
    return data
  } catch {
    const rawResponse = await response.text()
    throw new Error(rawResponse)
  }
}

/**
 * Get prepare results for the AI tree tool.
 *
 * @param treeId - The tree ID
 * @param treeVer - The tree version
 * @returns Promise<PrepareResults> - The response data from the prepare results
 */
export async function getPrepareResults(
  treeId: string,
  treeVer: string,
): Promise<PrepareResults> {
  const response = await makeContentServiceGetRequest(
    `content/v1/ai-tree-tool/${treeId}:${treeVer}/prepare/results`,
  )

  try {
    const data = await response.json()
    return data
  } catch {
    const rawResponse = await response.text()
    throw new Error(rawResponse)
  }
}

/**
 * Get process results for the AI tree tool.
 *
 * @param treeId - The tree ID
 * @param treeVer - The tree version
 * @returns Promise<ProcessResults> - The response data from the process results
 */
export async function getProcessResults(
  treeId: string,
  treeVer: string,
): Promise<ProcessResults> {
  const response = await makeContentServiceGetRequest(
    `content/v1/ai-tree-tool/${treeId}:${treeVer}/process/results`,
  )

  try {
    const data = await response.json()
    return data
  } catch {
    const rawResponse = await response.text()
    throw new Error(rawResponse)
  }
}

/**
 * Start the process stage of the AI tree tool pipeline.
 *
 * @param treeId - The original tree ID (URL parameter)
 * @param treeVer - The original tree version (URL parameter)
 * @param workTreeId - The working tree ID (body parameter)
 * @param workTreeVer - The working tree version (body parameter)
 * @param processMode - The process mode (ai-autocorrect or title-autocorrect)
 * @param area - The prompt area (category)
 * @param promptId - The prompt ID to use for processing
 * @param model - The model to use
 * @param provider - The provider to use
 * @param batchMode - The batch mode (max corrections per request)
 * @returns Promise<unknown> - The response data from the process stage
 */
export async function startProcess(
  treeId: string,
  treeVer: string,
  workTreeId: string,
  workTreeVer: string,
  processMode: ProcessMode,
  area: string,
  promptId: string,
  model: string,
  provider: string,
  batchMode: BatchMode,
): Promise<unknown> {
  const response = await makeContentServicePostRequest(
    `content/v1/ai-tree-tool/${treeId}:${treeVer}/process/start`,
    {
      work_tree_id: workTreeId,
      work_tree_ver: workTreeVer,
      process_mode: processMode,
      area,
      prompt_id: promptId,
      model,
      provider,
      batch_mode: batchMode,
    },
  )

  if (!response.ok) {
    const rawResponse = await response.text()
    const error = new Error(rawResponse) as Error & { status?: number }
    error.status = response.status
    throw error
  }

  try {
    const data = await response.json()
    return data
  } catch {
    const rawResponse = await response.text()
    throw new Error(rawResponse)
  }
}

/**
 * Start the apply stage of the AI tree tool pipeline.
 *
 * @param treeId - The original tree ID (URL parameter)
 * @param treeVer - The original tree version (URL parameter)
 * @param workTreeId - The working tree ID (body parameter)
 * @param workTreeVer - The working tree version (body parameter)
 * @param applyCorrections - The apply corrections from process results (body parameter)
 * @returns Promise<unknown> - The response data from the apply stage
 */
export async function startApply(
  treeId: string,
  treeVer: string,
  workTreeId: string,
  workTreeVer: string,
  applyCorrections: Record<string, unknown[]>,
): Promise<unknown> {
  const response = await makeContentServicePostRequest(
    `content/v1/ai-tree-tool/${treeId}:${treeVer}/apply/start`,
    {
      work_tree_id: workTreeId,
      work_tree_ver: workTreeVer,
      apply_corrections: applyCorrections,
    },
  )

  if (!response.ok) {
    const rawResponse = await response.text()
    const error = new Error(rawResponse) as Error & { status?: number }
    error.status = response.status
    throw error
  }

  try {
    const data = await response.json()
    return data
  } catch {
    const rawResponse = await response.text()
    throw new Error(rawResponse)
  }
}

/**
 * Get available v2 AI prompts for the tree tool.
 *
 * @param area - Optional area filter to limit prompts by area/category
 * @returns Promise<PromptsV2Response> - The response data containing available v2 prompts organized by area
 */
export async function getPromptsV2(area?: string): Promise<PromptsV2Response> {
  let path = 'content/v1/ai-tree-tool/prompts/v2'
  if (area) {
    path += `?area=${encodeURIComponent(area)}`
  }

  const response = await makeContentServiceGetRequest(path)

  try {
    const data = await response.json()
    return data
  } catch {
    const rawResponse = await response.text()
    throw new Error(rawResponse)
  }
}

/**
 * Run a v2 prompt with user messages.
 *
 * @param area - The area/category of the prompt
 * @param promptId - The unique identifier for the prompt
 * @param model - The model to use for execution
 * @param provider - The provider to use (e.g., "openrouter" or "openai")
 * @param userPrompts - A list of user prompt strings (always sent as an array)
 * @returns Promise<PromptRunResponse> - The response data from the prompt execution
 */
export async function runPrompt(
  area: string,
  promptId: string,
  model: string,
  provider: string,
  userPrompts: string[],
): Promise<PromptRunResponse> {
  // Ensure user_prompts is always an array
  const userPromptsArray = Array.isArray(userPrompts)
    ? userPrompts
    : [userPrompts]

  const response = await makeContentServicePostRequest(
    'content/v1/ai-tree-tool/prompts/v2/run',
    {
      area,
      prompt_id: promptId,
      model,
      provider,
      user_prompts: userPromptsArray,
    },
  )

  try {
    const data = await response.json()
    return data
  } catch {
    const rawResponse = await response.text()
    throw new Error(rawResponse)
  }
}

/**
 * Get the pipeline status for the AI tree tool.
 *
 * @param treeId - The tree ID
 * @param treeVer - The tree version
 * @returns Promise<PipelineStatusResponse> - The response data from the pipeline status
 */
export async function getPipelineStatus(
  treeId: string,
  treeVer: string,
): Promise<PipelineStatusResponse> {
  const response = await makeContentServiceGetRequest(
    `content/v1/ai-tree-tool/${treeId}:${treeVer}/pipeline/status`,
  )

  try {
    const data = await response.json()
    return data
  } catch {
    const rawResponse = await response.text()
    throw new Error(rawResponse)
  }
}

/**
 * Get apply results for the AI tree tool.
 *
 * @param treeId - The tree ID
 * @param treeVer - The tree version
 * @returns Promise<ApplyResults> - The response data from the apply results
 */
export async function getApplyResults(
  treeId: string,
  treeVer: string,
): Promise<ApplyResults> {
  const response = await makeContentServiceGetRequest(
    `content/v1/ai-tree-tool/${treeId}:${treeVer}/apply/results`,
  )

  try {
    const data = await response.json()
    return data
  } catch {
    const rawResponse = await response.text()
    throw new Error(rawResponse)
  }
}

/**
 * Remove the AI tree tool pipeline.
 *
 * @param treeId - The tree ID
 * @param treeVer - The tree version
 * @returns Promise<unknown> - The response data from the remove operation
 */
export async function removePipeline(
  treeId: string,
  treeVer: string,
): Promise<unknown> {
  const response = await makeContentServiceDeleteRequest(
    `content/v1/ai-tree-tool/${treeId}:${treeVer}/pipeline/delete`,
  )

  try {
    const data = await response.json()
    return data
  } catch {
    const rawResponse = await response.text()
    throw new Error(rawResponse)
  }
}

/**
 * Log process report for the AI tree tool.
 *
 * @param treeId - The tree ID
 * @param treeVer - The tree version
 * @param userEmail - The user email
 * @returns Promise<unknown> - The response data from the process report log
 */
export async function logProcessReport(
  treeId: string,
  treeVer: string,
  userEmail: string,
): Promise<unknown> {
  const response = await makeContentServicePostRequest(
    `content/v1/ai-tree-tool/${treeId}:${treeVer}/process/report/log`,
    { user_email: userEmail },
  )

  try {
    const data = await response.json()
    return data
  } catch {
    const rawResponse = await response.text()
    throw new Error(rawResponse)
  }
}
